import React, { ChangeEvent, useContext, useState } from "react";
import cookie from "react-cookies";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Input } from "@airhelp/react";
import { Button, Center, Heading, Link, Text, VStack } from "@chakra-ui/react";
import { HTTPError } from "ky";

import { authApi, userApi } from "api";
import config from "config/config";
import { USER_RETURN_AHPLUS_COOKIE } from "config/cookies";
import { SessionContext, TrackingContext } from "contexts";
import { ErrorBadge } from "elements";
import { ILoginSubmitData, IUser } from "models/User";
import { HttpResponse } from "types/api";
import { useTrackEvent } from "utils/tracking/hooks";
import { emailPattern } from "utils/validations";

const { authLogin } = authApi;
const { fetchSession } = userApi;

const LoginView: React.FC = () => {
  const { login } = useContext(SessionContext);
  const { setLoginData } = useContext(TrackingContext);
  const [apiError, setApiError] = useState<string | null>();
  const { t } = useTranslation();
  const { trackCtaClick } = useTrackEvent();
  const { handleSubmit, control, formState } = useForm<ILoginSubmitData>({ mode: "onChange" });
  const { isValid, errors } = formState;

  const isDisabled = !isValid;

  const saveCookieUserReturnUrl = () => {
    cookie.save(USER_RETURN_AHPLUS_COOKIE, "1", config.cookieOptions);
  };

  const loginUser = async (data: ILoginSubmitData): Promise<HttpResponse> => {
    return authLogin(data).then((response) => {
      const [error] = response;
      const errorResponse = error as HTTPError;
      if (errorResponse?.response.status === 401) {
        return response;
      }
      return fetchSession();
    });
  };

  const handleErrorMessage = async (errorResponse: HTTPError) => {
    let errorMessage;

    if (errorResponse?.response?.status === 401) {
      errorMessage = t("errors.invalid_email_or_password");
    } else {
      const errorBody = await errorResponse?.response?.json();
      errorMessage = errorBody?.error || t("errors.something_went_wrong");
    }

    return errorMessage;
  };

  const onSubmit = async (data: ILoginSubmitData) => {
    setApiError(null);

    const [error, response] = await loginUser(data);
    const session = error ? null : response.user;

    const errorResponse = error as HTTPError;

    if (session) {
      setLoginData({ method: "email", user: session as IUser });
      login(session as IUser);
    } else {
      const errorMessage = await handleErrorMessage(errorResponse);
      setApiError(errorMessage);
    }
  };

  return (
    <>
      <Heading variant="h4" mb={4} textAlign="left">
        {t("authentication.login_form.header")}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} mb={10}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              pattern: { value: emailPattern, message: t("errors.email_invalid") },
            }}
            defaultValue=""
            render={({ field: { onChange, name } }) => (
              <Input
                isInvalid={!!errors.email}
                errorMessage={errors.email?.message || ""}
                placeholder="youremail@domain.com"
                size="lg"
                label={t("authentication.email_address")}
                name={name}
                type="email"
                id="email"
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                autoComplete="username"
                data-testid="email"
              />
            )}
          />

          <Controller
            render={({ field: { onChange, name } }) => (
              <Input
                isInvalid={!!errors.password}
                errorMessage={errors.password?.message || ""}
                placeholder="********"
                size="lg"
                label={t("authentication.password")}
                name={name}
                type="password"
                id="password"
                autoComplete="current-password"
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                data-testid="password"
              />
            )}
            name="password"
            control={control}
            rules={{ required: { value: true, message: t("errors.required") } }}
            defaultValue=""
          />

          {apiError && <ErrorBadge>{apiError}</ErrorBadge>}
        </VStack>

        <Button
          size="s"
          width="100%"
          onClick={() => {
            trackCtaClick("login", "login page");
            handleSubmit(onSubmit)();
          }}
          isDisabled={isDisabled}
          data-testid="login-submit"
        >
          {t("authentication.login_form.submit")}
        </Button>

        <Center mt={4}>
          <Text fontSize="md">
            {t("authentication.forgot_your_password")}
            <Link
              href={config.webappResetPasswordUrl}
              onClick={() => {
                trackCtaClick("reset password", "login page");
                saveCookieUserReturnUrl();
              }}
              pl={2}
            >
              {t("authentication.reset_it_now")}
            </Link>
          </Text>
        </Center>
      </form>
    </>
  );
};

export default LoginView;
